import { takeEvery, call, put, select, all, delay } from 'redux-saga/effects';
import { defineMessages } from 'react-intl';
import { reset, initialize } from 'redux-form';
import braze, { BRAZE_CUSTOM_EVENTS } from 'helpers/braze';
import Segment from 'helpers/segment';
import { fetchEntity, fetchEntityIfNeeded } from '@classpass/saga-tools';
import { SpotsUpdateRequest } from '@classpass/protobuf-definitions/lib/web/models/partners/schedule';
import _isNil from 'lodash/isNil';
import snakeCase from 'lodash/snakeCase';

import { selectColumnFocus } from 'features/App/ColumnLayout.selectors';

import { EMAIL_TYPES, ENABLE_RECURRENCE, NETWORK_CAP_RECURRENCE } from 'constants/logic';

import { EMAIL_MESSAGE_TYPE_BY_EMAIL_TYPE, OPPORTUNITY_ACT_SOURCE } from 'constants/tracking';
import URLS from 'constants/urls';
import { userHasAccessToVenue, getFirstLinkedVenue } from 'helpers/active-venue';
import { transformKeysToSnakeCase } from 'helpers/core';
import { reportError } from 'helpers/error-logging';
import { makePath } from 'helpers/link-generation';
import { queryClient } from 'helpers/query-client';
import {
  roster,
  scheduleInsights,
  userInfo,
  venueOpportunities,
  waivers,
  scheduleData,
  excessInventory as excessInventoryEntity,
} from 'store/saga/entities/bound';
import {
  selectActiveVenueId,
  selectActiveVenue,
  selectRouteParams,
  selectSchedulesStartDate,
  selectUserId,
} from 'selectors/general';
import { selectIsDesktop } from 'selectors/ui';
import { createInfoBanner, createErrorBanner } from 'components/Banner';
import { closeModal, waitForModal } from 'components/Modal';
import {
  OPTIMIZELY_FEATURE_FLAGS_KEYS,
  PARTNERS_SCHEDULE_V2_ROLLOUT,
  SCHEDULE_DETAILS_V2,
} from 'components/Optimizely/Optimizely.features';
import { isFeatureEnabledForUser } from 'components/Optimizely/Optimizely.sdk';
import { createSuccessSnackbar, createErrorSnackbar } from 'components/Snackbar';
import { doFetchVenueSmartspotData } from 'components/SmartSpot';
import { ACTIVE_VENUE_CHANGED, getStartDateOrToday, waitForAppInit, waitForNavigate } from 'features/App/App.saga';

import { setSchedulesStartDate } from 'reducers/app';

import {
  trackAcceptedOpportunity,
  trackEnabledClass,
  trackAllocationChange,
  trackScheduleReservationsStopped,
  trackScheduleReservationsStoppedError,
  trackOpportunityAccepted,
} from 'helpers/events';

import { refreshVenueSchedulesCacheForVenueAndDate } from './Contexts/VenueSchedulesCache';
import { scheduleDetailsKeys } from './ScheduleDetailV2/ScheduleDetailV2.hooks';
import { setSelectedScheduleId, removeSelectedScheduleId, setLastActiveVenueId } from './YieldManagement.reducers';
import {
  selectActiveScheduleId,
  selectActiveScheduleDetailTab,
  selectLastActiveVenueId,
} from './YieldManagement.selectors';
import { setWeeksCount } from '../Home/modules/ExcessInventory/ExcessInventory.reducers';

export const FETCH_VENUE_DATA = 'FETCH_VENUE_DATA';
export const FETCH_SCHEDULE_DATA = 'FETCH_SCHEDULE_DATA';
export const SET_SELECTED_SCHEDULE = 'SET_SELECTED_SCHEDULE';
export const SET_SELECTED_TAB = 'SET_SELECTED_TAB';
export const UPDATE_START_DATE = 'UPDATE_START_DATE';
export const UPDATE_SCHEDULE_NETWORK_ALLOCATION = 'UPDATE_SCHEDULE_NETWORK_ALLOCATION';
export const CANCEL_SCHEDULE = 'CANCEL_SCHEDULE';
export const ENABLE_SCHEDULE = 'ENABLE_SCHEDULE';
export const UPDATE_RESERVATION_ATTENDANCE = 'UPDATE_RESERVATION_ATTENDANCE';
export const UPDATE_RESERVATION_FEEDBACK = 'UPDATE_RESERVATION_FEEDBACK';
export const ACT_ON_OPPORTUNITY_FROM_SCHEDULE = 'ACT_ON_OPPORTUNITY_FROM_SCHEDULE';
export const EMAIL_SCHEDULE_ATTENDEES = 'EMAIL_SCHEDULE_ATTENDEES';
export const UPDATE_SCHEDULE_RESERVATIONS_STOPPED = 'UPDATE_SCHEDULE_RESERVATIONS_STOPPED';
export const UPDATE_SCHEDULE_RESERVATIONS_NOT_STOPPED = 'UPDATE_SCHEDULE_RESERVATIONS_NOT_STOPPED';
export const REDIRECT_TO_MANAGE_CLASS = 'REDIRECT_TO_MANAGE_CLASS';
export const REDIRECT_TO_MANAGE_SCHEDULE = 'REDIRECT_TO_MANAGE_SCHEDULE';
export const OPEN_CANCEL_SCHEDULE_MODAL = 'OPEN_CANCEL_SCHEDULE_MODAL';
export const ADD_SPOT = 'ADD_SPOT';
export const EMAIL_MEMBER = 'EMAIL_MEMBER';
export const ROSTER_PAGE_SOURCE = 'roster';
export const REFRESH_ROSTER_AFTER_RESERVATION_FEEDBACK = 'YIELD_MANAGEMENT/REFRESH_ROSTER_AFTER_RESERVATION_FEEDBACK';
export const refreshRosterAfterReservationFeedback = ({ venueId, scheduleId } = {}) => ({
  type: REFRESH_ROSTER_AFTER_RESERVATION_FEEDBACK,
  venueId,
  scheduleId,
});

export const fetchVenueData = (venueId, startDate, scheduleId) => ({
  type: FETCH_VENUE_DATA,
  venueId,
  startDate,
  scheduleId,
});
export const fetchScheduleData = (venueId, scheduleId, refresh = false) => ({
  type: FETCH_SCHEDULE_DATA,
  venueId,
  scheduleId,
  refresh,
});
export const setSelectedSchedule = ({ venueId, startDate, scheduleId, tab, focus }) => ({
  type: SET_SELECTED_SCHEDULE,
  venueId,
  startDate,
  scheduleId,
  tab,
  focus,
});
export const setSelectedTab = (venueId, scheduleId, tab) => ({
  type: SET_SELECTED_TAB,
  venueId,
  scheduleId,
  tab,
});

export const updateStartDate = startDate => ({ type: UPDATE_START_DATE, startDate });
export const updateScheduleNetworkAllocation = (venueId, data, isDesktop, formName, formAsyncFns) => ({
  type: UPDATE_SCHEDULE_NETWORK_ALLOCATION,
  venueId,
  ...data,
  isDesktop,
  formName,
  ...formAsyncFns,
});
export const cancelSchedule = (schedule, isRecurring, additionalDetails, cancellationReason, message) => ({
  type: CANCEL_SCHEDULE,
  schedule,
  isRecurring,
  additionalDetails,
  cancellationReason,
  message,
});
export const enableSchedule = (venueId, schedule, classDisabled, smartspotEnabled, classpassSpots, resolveSubmit) => ({
  type: ENABLE_SCHEDULE,
  venueId,
  schedule,
  classDisabled,
  smartspotEnabled,
  classpassSpots,
  resolveSubmit,
});
export const updateReservationAttendance = (scheduleId, reservationId, attended) => ({
  type: UPDATE_RESERVATION_ATTENDANCE,
  scheduleId,
  reservationId,
  attended,
});
export const updateReservationFeedback = (formValues, formAsyncFns) => ({
  type: UPDATE_RESERVATION_FEEDBACK,
  ...formValues,
  ...formAsyncFns,
});
export const emailScheduleAttendees = (values, formAsyncFns) => ({
  type: EMAIL_SCHEDULE_ATTENDEES,
  ...values,
  ...formAsyncFns,
});
export const emailMember = (values, formAsyncFns) => ({
  type: EMAIL_MEMBER,
  ...values,
  ...formAsyncFns,
});

export const updateScheduleReservationsStopped = scheduleId => ({
  type: UPDATE_SCHEDULE_RESERVATIONS_STOPPED,
  scheduleId,
});
export const updateScheduleReservationsNotStopped = scheduleId => ({
  type: UPDATE_SCHEDULE_RESERVATIONS_NOT_STOPPED,
  scheduleId,
});

export const actOnOpportunityFromSchedule = ({
  venueId,
  schedule,
  opportunities,
  spotsAdded,
  className,
  startDateTime,
  source,
  showDemand,
}) => ({
  type: ACT_ON_OPPORTUNITY_FROM_SCHEDULE,
  venueId,
  schedule,
  opportunities,
  spotsAdded,
  className,
  startDateTime,
  source,
  showDemand,
});

export const redirectToManageClass = (venueId, classId) => ({
  type: REDIRECT_TO_MANAGE_CLASS,
  venueId,
  classId,
});

export const redirectToManageSchedule = ({ apiGuid, venueId, startDate, startDateTime }) => ({
  type: REDIRECT_TO_MANAGE_SCHEDULE,
  apiGuid,
  venueId,
  startDate,
  startDateTime,
});

export const openCancelScheduleModal = payload => ({ type: OPEN_CANCEL_SCHEDULE_MODAL, payload });

export const addSpot = (networkCap, scheduleId, spots = 1, smartspotEnabled = false, showDemand = false) => ({
  type: ADD_SPOT,
  networkCap,
  scheduleId,
  spots,
  smartspotEnabled,
  showDemand,
});

export const messages = {
  success: defineMessages({
    scheduleCancelled: {
      id: 'yield-management.saga.do-cancel-schedule.success-single',
      defaultMessage: '{scheduleName} has been canceled.',
    },
    allSchedulesCancelled: {
      id: 'yield-management.saga.do-cancel-schedule.success-recurring',
      defaultMessage: 'All upcoming {scheduleName} sessions have been canceled.',
    },
    allocationUpdated: {
      id: 'yield-management.saga.do-update-schedule-network-allocation.success',
      defaultMessage:
        '{scheduleName} has been updated to have {networkCap, plural, one {# network spot} other {# network spots}}.',
    },
    scheduleEnabledWithSmartspot: {
      id: 'yield-management.saga.do-enable-schedule.success-schedule-smartspot',
      defaultMessage: '{scheduleName} has been enabled with SmartSpot recommended number of network spots.',
    },
    classEnabledWithSmartspot: {
      id: 'yield-management.saga.do-enable-schedule.success-class-smartspot',
      defaultMessage:
        '{scheduleName} and all future instances of this session have been enabled with SmartSpot recommended number of network spots.',
    },
    scheduleEnabledWithNetworkCap: {
      id: 'yield-management.saga.do-enable-schedule.success-schedule-network-cap',
      defaultMessage:
        '{scheduleName} has been enabled with {networkCap, plural, one {# network spot} other {# network spots}}.',
    },
    classEnabledWithNetworkCap: {
      id: 'yield-management.saga.do-enable-schedule.success-class-network-cap',
      defaultMessage:
        '{scheduleName} and all future instances of this session have been enabled with {networkCap, plural, one {# network spot} other {# network spots}}.',
    },
    classEnabledNoNetworkCapSingleInstance: {
      id: 'yield-management.saga.do-enable-schedule.success-no-network-cap-single-instance',
      defaultMessage: '{scheduleName} has been enabled.',
    },
    classEnabledNoNetworkCapRecurring: {
      id: 'yield-management.saga.do-enable-schedule.success-no-network-cap-recurring',
      defaultMessage: 'This and all future instances of {scheduleName} have been enabled.',
    },
    scheduleEnabledWithNetworkCapAndFutureNetworkCaps: {
      id: 'yield-management.saga.do-enable-schedule.success-schedule-network-cap-update-future',
      defaultMessage:
        '{scheduleName} has been enabled with {networkCap, plural, one {# network spot} other {# network spots}} and future sessions’ network spots have been updated.',
    },
    classEnabledWithNetworkCapAndFutureNetworkCaps: {
      id: 'yield-management.saga.do-enable-schedule.success-class-network-cap-update-future',
      defaultMessage:
        '{scheduleName} and all future instances of this session have been enabled with {networkCap, plural, one {# network spot} other {# network spots}} and future sessions’ network spots have been updated.',
    },
    attendanceMarked: {
      id: 'yield-management.saga.do-update-reservation-attendance.success',
      defaultMessage: 'Attendance marked',
    },
    feedbackSubmitted: {
      id: 'yield-management.saga.do-update-reservation-feedback.success',
      defaultMessage: 'Your feedback was successfully submitted to ClassPass',
    },
    classMarkedAsFull: {
      id: 'yield-management.saga.do-update-schedule-status.class-marked-as-full',
      defaultMessage: 'Session was successfully marked as full',
    },
    classMarkedAsNotFull: {
      id: 'yield-management.saga.do-update-schedule-status.class-marked-as-not-full',
      defaultMessage: 'Session was successfully marked as no longer full',
    },
    sendingEmail: {
      id: 'yield-management.saga.do-email-schedule-attendees.sending-email',
      defaultMessage: 'Message sent',
    },
    addSpot: {
      id: 'yield-management.saga.do-add-spot.success',
      defaultMessage: 'A spot was successfully added to the schedule.',
    },
    spotsAdded: {
      id: 'yield-management.saga.do-act-on-opportunity-from-schedule.success',
      defaultMessage:
        '{spotsAdded, plural, one {A spot was successfully added to the schedule.} other {# spots were successfully added to the schedule.}}',
    },
  }),
  error: defineMessages({
    fetchingScheduleData: {
      id: 'yield-management.saga.do-fetch-venue-schedules.error',
      defaultMessage: 'There was a problem fetching Schedule data.',
    },
    markingAttendance: {
      id: 'yield-management.saga.do-update-reservation-attendance.error',
      defaultMessage: 'There was a problem marking the user’s attendance. Please try again.',
    },
    submittingFeedback: {
      id: 'yield-management.saga.do-update-reservation-feedback.error',
      defaultMessage: 'There was a problem submitting feedback for the user. Please try again.',
    },
    feedbackAlreadyExists: {
      id: 'yield-management.saga.do-update-reservation-feedback-exists.error',
      defaultMessage: `Feedback was already recorded for this member's reservation.`,
    },
    sendingEmail: {
      id: 'yield-management.saga.do-email-schedule-attendees.error',
      defaultMessage: 'An error occured.',
    },
    updatingScheduleStatus: {
      id: 'yield-management.saga.do-update-schedule-status.error',
      defaultMessage: 'Something went wrong, please try again later',
    },
    redirectToManageClass: {
      id: 'yield-management.saga.do-redirect-to-manage-class.error',
      defaultMessage: 'There was a problem redirecting to Manage Session page.',
    },
    redirectToManageSchedule: {
      id: 'yield-management.saga.do-redirect-to-manage-schedule.error',
      defaultMessage: 'There was a problem redirecting to Manage Schedule page.',
    },
    addSpot: {
      id: 'yield-management.saga.do-add-spot.error',
      defaultMessage: 'There was a problem adding a spot to the schedule. Please try again later.',
    },
    allocationUpdate: {
      id: 'yield-management.saga.do-update-schedule-network-allocation.error',
      defaultMessage: 'There was a problem updating the network spots for this schedule. Please try again later.',
    },
    allocationUpdateBelowTakenSpots: {
      id: 'yield-management.saga.do-update-schedule-network-allocation.error-below-taken-spots',
      defaultMessage:
        'Network spots can’t be reduced below the number of spots already taken. The schedule has been refreshed with the latest availability.',
    },
    allocationUpdateNotEnoughCapacity: {
      id: 'yield-management.saga.do-update-schedule-network-allocation.error-not-enough-capacity',
      defaultMessage:
        'This schedule doesn’t have enough open spots for that allocation. The schedule has been refreshed with the latest availability.',
    },
    enableSchedule: {
      id: 'yield-management.saga.do-enable-schedule.error',
      defaultMessage: 'There was a problem enabling the schedule. Please try again.',
    },
    enableScheduleUnsupportedVenueSource: {
      id: 'yield-management.saga.do-enable-schedule-unsupported-venue-source.error',
      defaultMessage: 'Schedules for this venue cannot be enabled from the dashboard.',
    },
  }),
  email: defineMessages({
    subject: {
      id: 'yield-management.saga.do-email-schedule-attendees.subject',
      defaultMessage:
        'Update regarding your {startDateTime, date, time}, {startDateTime, date, long-w-dow} session at {venueName}{venueSubtitle, select, undefined {} other { ({venueSubtitle})}}',
    },
  }),
};

export function* isScheduleDetailsV2EnabledForVenue(venueId) {
  const userId = yield select(selectUserId);

  try {
    const isInRollout = yield call(
      isFeatureEnabledForUser,
      OPTIMIZELY_FEATURE_FLAGS_KEYS[PARTNERS_SCHEDULE_V2_ROLLOUT],
      userId
    );
    if (!isInRollout) return false;

    return yield call(isFeatureEnabledForUser, OPTIMIZELY_FEATURE_FLAGS_KEYS[SCHEDULE_DETAILS_V2], userId, {
      active_venue_id: venueId,
    });
  } catch (error) {
    yield call(reportError, 'Failed to resolve schedule-details-v2 flag', { error, userId, venueId });
    return false;
  }
}

export function* doFetchScheduleData(client, action) {
  const { venueId, scheduleId, refresh } = action;

  // Details v2 reads roster/insights via React Query; skipping here avoids duplicate requests.
  // After a write (attendance, cancellation) the caller passes refresh so the RQ caches refetch.
  const isDetailsV2Enabled = yield call(isScheduleDetailsV2EnabledForVenue, venueId);
  if (isDetailsV2Enabled) {
    if (refresh) {
      yield call([queryClient, queryClient.invalidateQueries], { queryKey: scheduleDetailsKeys.all });
    }
    return;
  }

  yield all([
    call(fetchEntity, roster, [client, venueId, scheduleId]),
    call(fetchEntity, scheduleInsights, [client, venueId, scheduleId], { shouldThrow: false }),
    call(fetchEntity, excessInventoryEntity, [client, { venueId, weeksCount: 1, scheduleId }]),
    put(setWeeksCount(1)),
  ]);
}

export function* invalidateScheduleDetailsV2Reads(venueId) {
  const isDetailsV2Enabled = yield call(isScheduleDetailsV2EnabledForVenue, venueId);
  if (isDetailsV2Enabled) {
    yield call([queryClient, queryClient.invalidateQueries], { queryKey: scheduleDetailsKeys.all });
  }
}

export function* doFetchVenueData(dependencies, action) {
  yield call(waitForAppInit);
  let { venueId, startDate } = action;

  if (!venueId) {
    venueId = yield select(selectActiveVenueId);
  }

  if (!venueId) return;

  if (!startDate) {
    startDate = yield select(selectSchedulesStartDate);
  }

  yield all([
    call(doFetchVenueSmartspotData, dependencies, { venueId, startDate }),
    call(fetchEntityIfNeeded, waivers, [dependencies, waivers.method.latest, venueId]),
  ]);
}

export function* doUpdateStartDate(client, action) {
  const { startDate } = action;
  let { venueId } = action;

  if (!venueId) {
    venueId = yield select(selectActiveVenueId);
  }

  const newPath = makePath.classes({ venueId, startDate });

  yield all([
    put(setSchedulesStartDate(startDate)),
    put(removeSelectedScheduleId()),
    call(client.navigate, newPath),
    call(doFetchVenueSmartspotData, client, { venueId, startDate }),
  ]);
}

export function* doSetSelectedSchedule(client, action) {
  const { scheduleId, tab = 'roster', focus = 'list' } = action;
  let { venueId, startDate } = action;

  if (!venueId) {
    venueId = yield select(selectActiveVenueId);
  }

  if (!startDate) {
    startDate = yield select(selectSchedulesStartDate);
  }

  const newPath = makePath.classes({ venueId, startDate, scheduleId, tab, focus });
  yield all([put(setSelectedScheduleId(scheduleId)), call(client.navigate, newPath)]);
}

export function* doSetSelectedTab(client, action) {
  const { venueId, scheduleId, tab } = action;

  if (tab === 'roster') {
    yield call(Segment.event, 'Click Roster', { scheduleId, venueId });
  }
  if (tab === 'insights') {
    yield call(Segment.event, 'Click SS Insights', { scheduleId, venueId });
  }

  const startDate = yield select(selectSchedulesStartDate);
  const focus = yield select(selectColumnFocus);
  const newPath = makePath.classes({ venueId, startDate, scheduleId, tab, focus });
  yield call(client.navigate, newPath);
}

export function* doCancelSchedule(client, action) {
  const { httpClient, intl } = client;
  const { schedule, isRecurring, additionalDetails = '', cancellationReason, message = '' } = action;
  const { isInThePast, startDateTime, scheduleId, scheduleName } = schedule;
  const venueId = yield select(selectActiveVenueId);
  const { venueName, venueSubtitle } = yield select(selectActiveVenue);
  const cancelEndpoint = URLS.schedule.cancel(scheduleId);

  const payload = {
    message: {
      internalMessage: additionalDetails,
      messageType: EMAIL_TYPES.CANCEL,
      messageReason: cancellationReason,
      messageContent: message.replace(/\n/g, '<br/>'),
      subject: intl.formatMessage(messages.email.subject, {
        startDateTime,
        venueName,
        venueSubtitle: venueSubtitle || undefined,
      }),
    },
    withAllFutures: isRecurring,
  };

  yield call([httpClient, httpClient.post], cancelEndpoint, transformKeysToSnakeCase(payload));
  if (isInThePast) {
    yield call(Segment.event, 'Class Cancelled - Post', {
      reason: cancellationReason,
      scheduleId,
      venueId,
    });
  }

  yield all([
    call(doFetchVenueData, client, { venueId }),
    call(refreshVenueSchedulesCacheForVenueAndDate, { venueId, date: startDateTime }),
  ]);
  yield put(closeModal());
  if (!isRecurring) {
    yield all([
      put(createInfoBanner(messages.success.scheduleCancelled, { scheduleName })),
      call(braze.trackEvent, BRAZE_CUSTOM_EVENTS.CLASS_CANCELLED),
      call(Segment.event, 'Class Cancelled', {
        scheduleId,
        venueId,
        venueName,
      }),
    ]);
  } else {
    yield all([
      put(createInfoBanner(messages.success.allSchedulesCancelled, { scheduleName })),
      call(braze.trackEvent, BRAZE_CUSTOM_EVENTS.CLASS_CANCELLED),
      call(Segment.event, 'Class Cancelled - Future Instances', {
        scheduleId,
        venueId,
        venueName,
      }),
    ]);
  }
}

export function* getMsiScheduleLink(client, action) {
  const { httpClient } = client;
  const { schedule = {} } = action;
  const { venueId, startDateTime, apiGuid } = schedule;

  try {
    if (!apiGuid) {
      return null;
    }

    const endpoint = URLS.msSchedules.getParentMSIScheduleId(venueId, apiGuid);

    const {
      data: { schedule_id },
    } = yield call([httpClient, httpClient.get], endpoint);

    return makePath.schedulesEdit({ venueId, startDate: startDateTime, id: schedule_id });
  } catch (error) {
    return makePath.schedules({ venueId });
  }
}

// Maps known partners-service 400 validation messages (see
// ScheduleController.updateScheduleSpotsInstance) to user-facing intl messages.
// Error names come from partners-service BusinessException subclasses serialized by PartnersExceptionMapper.
export const getAllocationUpdateErrorMessage = error => {
  const errorName = error?.response?.data?.name;

  if (errorName === 'SPOTS_BELOW_TAKEN_SPOTS') {
    return messages.error.allocationUpdateBelowTakenSpots;
  }
  if (errorName === 'SPOTS_EXCEED_CAPACITY') {
    return messages.error.allocationUpdateNotEnoughCapacity;
  }

  return messages.error.allocationUpdate;
};

export function* doUpdateScheduleNetworkAllocation(client, action) {
  try {
    const { httpClient } = client;
    const {
      schedule,
      venueId,
      actedOnOpportunity,
      classpassSpots,
      opportunities,
      spotsAdded,
      smartspotEnabled,
      isDesktop,
      formName,
      resolveSubmit,
    } = action;
    const requiresConfirmation = classpassSpots === 0;
    const { scheduleId, scheduleName } = schedule;

    let hasConfirmed = false;
    if (requiresConfirmation) {
      hasConfirmed = yield call(waitForModal, client, 'PremiumOnlyModal', { schedule });
    } else {
      hasConfirmed = true;
    }

    if (!hasConfirmed) {
      resolveSubmit();
      yield put(reset(formName));
      return;
    }

    if (actedOnOpportunity) {
      yield call(fetchEntity, venueOpportunities, [client, { venueId, scheduleId, opportunities, spotsAdded }]);
      yield call(trackAcceptedOpportunity, 'schedule detail', scheduleId, spotsAdded);
    } else {
      const recurrence = isDesktop
        ? yield call(waitForModal, client, 'NetworkCapRecurrenceModal', { networkCap: classpassSpots, schedule })
        : NETWORK_CAP_RECURRENCE.THIS_INSTANCE;

      if (!recurrence) {
        resolveSubmit();
        yield put(reset(formName));
        return;
      }

      const endpoint = URLS.schedule.updateSpots(venueId, scheduleId);
      const requestBody = SpotsUpdateRequest.toJSON({
        spots: classpassSpots,
        ss_enabled: smartspotEnabled,
        with_all_futures: recurrence === NETWORK_CAP_RECURRENCE.ALL_FUTURE_INSTANCES,
      });

      if (_isNil(classpassSpots)) {
        // eslint-disable-next-line no-throw-literal
        throw { venueId, scheduleId, classpassSpots, recurrence };
      }

      yield call([httpClient, httpClient.put], endpoint, requestBody);
      yield call(
        trackAllocationChange,
        'schedule detail network allocation picker',
        scheduleId,
        classpassSpots,
        classpassSpots - schedule?.spots?.classpassTotal
      );
    }

    const startDate = yield select(selectSchedulesStartDate);
    yield all([
      call(doFetchVenueData, client, { venueId }),
      call(refreshVenueSchedulesCacheForVenueAndDate, { venueId, date: startDate }),
      call(invalidateScheduleDetailsV2Reads, venueId),
    ]);

    yield all([
      call(resolveSubmit),
      put(createInfoBanner(messages.success.allocationUpdated, { scheduleName, networkCap: classpassSpots })),
      // re-init the form making sure subsequent changes are cap changes, not acted-on opportunities
      put(initialize(formName, { classpassSpots, actedOnOpportunity: false })),
    ]);
  } catch (error) {
    const { venueId, formName, rejectSubmit } = action;
    yield call(rejectSubmit);
    yield put(createErrorBanner(getAllocationUpdateErrorMessage(error)));
    if (error instanceof Error) {
      yield call(reportError, error);
    } else {
      yield call(reportError, 'caught doUpdateScheduleNetworkAllocation', error);
    }

    // A 400 here usually means our capacity data went stale (e.g. spots were
    // taken between render and submit). Revert the picker to the saved value
    // and refresh schedule data so the next attempt uses current availability.
    yield put(reset(formName));
    try {
      const startDate = yield select(selectSchedulesStartDate);
      yield all([
        call(doFetchVenueData, client, { venueId }),
        call(refreshVenueSchedulesCacheForVenueAndDate, { venueId, date: startDate }),
      ]);
    } catch (refreshError) {
      yield call(reportError, refreshError);
    }
  }
}

export function* doOpenCancelScheduleModal(client, action) {
  try {
    const { payload } = action;
    const { isRecurring, activeSchedule, cancelSchedule: cancelScheduleAction, isMsInventoryVenue } = payload;

    let msiScheduleLink;
    if (isMsInventoryVenue) {
      msiScheduleLink = yield call(getMsiScheduleLink, client, { schedule: activeSchedule });
    }

    yield call(waitForModal, client, 'CancelScheduleModal', {
      isRecurring,
      activeSchedule,
      cancelSchedule: cancelScheduleAction,
      msiScheduleLink,
    });
  } catch (error) {
    yield call(reportError, 'caught doOpenCancelScheduleModal', error);
  }
}

export function* doAddSpot(client, action) {
  try {
    const { httpClient } = client;
    const { networkCap, scheduleId, spots, smartspotEnabled = false, showDemand = false } = action;
    const venueId = yield select(selectActiveVenueId);

    const endpoint = URLS.schedule.updateSpots(venueId, scheduleId);
    const requestBody = SpotsUpdateRequest.toJSON({
      spots,
      ss_enabled: smartspotEnabled,
      with_all_futures: false,
    });

    const segmentEvent = smartspotEnabled ? 'SmartSpot Spot Added' : 'Spot Added';

    yield call([httpClient, httpClient.put], endpoint, requestBody);

    const startDate = yield select(selectSchedulesStartDate);
    yield all([
      call(doFetchVenueData, client, { venueId }),
      call(refreshVenueSchedulesCacheForVenueAndDate, { venueId, date: startDate }),
      call(invalidateScheduleDetailsV2Reads, venueId),
    ]);
    yield all([
      put(createInfoBanner(messages.success.addSpot)),
      call(Segment.event, segmentEvent, {
        oldSpotAllocation: networkCap,
        newSpotAllocation: spots,
        scheduleId,
        venueId,
      }),
    ]);
    if (showDemand) {
      yield call(Segment.event, 'Demand Insights Spot Added', {
        scheduleId,
        venueId,
      });
    }
  } catch (error) {
    yield all([put(createErrorBanner(messages.error.addSpot)), call(reportError, error)]);
  }
}

const isVenueSourceUnsupported = ({ response }) => response && response.status === 409;

export function* doEnableSchedule(client, action) {
  const { httpClient } = client;
  const { venueId, schedule, classpassSpots, classDisabled, smartspotEnabled, resolveSubmit } = action;
  const { scheduleId, scheduleName, startDateTime } = schedule;
  const enableAtClassOrScheduleLevel = classDisabled ? ENABLE_RECURRENCE.CLASS_LEVEL : ENABLE_RECURRENCE.SCHEDULE_LEVEL;
  const enableEndpoint = URLS.schedule.enable(venueId, scheduleId);
  const updateNetworkCap = !_isNil(classpassSpots);
  let recurrence;
  let networkCapRecurrence;

  try {
    if (smartspotEnabled) {
      yield call([httpClient, httpClient.post], enableEndpoint, { update_option: enableAtClassOrScheduleLevel });
      networkCapRecurrence = 'SmartSpot';
    } else {
      const isDesktop = yield select(selectIsDesktop);
      const isEnablingSchedule = true;
      recurrence = isDesktop
        ? yield call(waitForModal, client, 'NetworkCapRecurrenceModal', {
            networkCap: classpassSpots,
            isEnablingSchedule,
            schedule,
          })
        : NETWORK_CAP_RECURRENCE.THIS_INSTANCE;
      networkCapRecurrence = recurrence;

      if (!recurrence) {
        resolveSubmit();
        return;
      }

      yield call([httpClient, httpClient.post], enableEndpoint, { update_option: enableAtClassOrScheduleLevel });

      if (updateNetworkCap) {
        const updateSpotsEndpoint = URLS.schedule.updateSpots(venueId, scheduleId);
        const requestBody = SpotsUpdateRequest.toJSON({
          spots: classpassSpots,
          ss_enabled: smartspotEnabled,
          with_all_futures: recurrence === NETWORK_CAP_RECURRENCE.ALL_FUTURE_INSTANCES,
        });
        yield call([httpClient, httpClient.put], updateSpotsEndpoint, requestBody);
      }
    }

    // Enable succeeded past this point, this is just a refresh failure that must not surface as an enable error.
    try {
      yield all([
        call(doFetchVenueData, client, { venueId }),
        call(refreshVenueSchedulesCacheForVenueAndDate, { venueId, date: startDateTime }),
      ]);
    } catch (refreshError) {
      yield call(reportError, refreshError);
    }

    let messageData;
    if (smartspotEnabled && classDisabled && !recurrence) {
      messageData = [messages.success.classEnabledWithSmartspot, { scheduleName }];
    } else if (smartspotEnabled && !classDisabled && !recurrence) {
      messageData = [messages.success.scheduleEnabledWithSmartspot, { scheduleName }];
    } else if (!smartspotEnabled && !updateNetworkCap && recurrence === 'instance') {
      messageData = [messages.success.classEnabledNoNetworkCapSingleInstance, { scheduleName }];
    } else if (!smartspotEnabled && !updateNetworkCap && recurrence === 'all_future') {
      messageData = [messages.success.classEnabledNoNetworkCapRecurring, { scheduleName }];
    } else if (!smartspotEnabled && classDisabled && recurrence === 'instance') {
      messageData = [messages.success.classEnabledWithNetworkCap, { scheduleName, networkCap: classpassSpots }];
    } else if (!smartspotEnabled && classDisabled && recurrence === 'all_future') {
      messageData = [
        messages.success.classEnabledWithNetworkCapAndFutureNetworkCaps,
        { scheduleName, networkCap: classpassSpots },
      ];
    } else if (!smartspotEnabled && !classDisabled && recurrence === 'instance') {
      messageData = [messages.success.scheduleEnabledWithNetworkCap, { scheduleName, networkCap: classpassSpots }];
    } else if (!smartspotEnabled && !classDisabled && recurrence === 'all_future') {
      messageData = [
        messages.success.scheduleEnabledWithNetworkCapAndFutureNetworkCaps,
        { scheduleName, networkCap: classpassSpots },
      ];
    }
    yield call(resolveSubmit);
    yield put(createInfoBanner(...messageData));
    yield call(
      trackEnabledClass,
      'empty schedule detail',
      scheduleId,
      enableAtClassOrScheduleLevel,
      networkCapRecurrence,
      classpassSpots
    );
  } catch (error) {
    if (isVenueSourceUnsupported(error)) {
      yield put(createErrorBanner(messages.error.enableScheduleUnsupportedVenueSource));
    } else {
      yield put(createErrorBanner(messages.error.enableSchedule));
    }
    yield call(reportError, error);
    yield call(resolveSubmit);
  }
}

export function* doUpdateReservationAttendance(client, action) {
  const venueId = yield select(selectActiveVenueId);
  const { scheduleId } = action;
  try {
    const { httpClient } = client;
    const { reservationId, attended } = action;
    const endpoint = URLS.schedule.updateReservationAttendance(venueId, reservationId);
    yield call([httpClient, httpClient.put], endpoint, { attendance_status: attended });
    yield call(doFetchScheduleData, client, { venueId, scheduleId, refresh: true });
    yield put(createInfoBanner(messages.success.attendanceMarked));
    yield call(Segment.event, 'Attendance', { venueId, reservationId, status: attended ? 'Attended' : 'Missed' });
  } catch (error) {
    const { response } = error;
    yield all([put(createErrorBanner(messages.error.markingAttendance)), call(reportError, error)]);
    // A 400 status means the reservation has been cancelled by user, reloads schedule data to get latest status
    if (response && response.status === 400) {
      yield call(doFetchScheduleData, client, { venueId, scheduleId, refresh: true });
    }
  }
}

export function* doUpdateReservationFeedback(client, action) {
  try {
    const { httpClient } = client;
    const { reservationId, feedbackOptions, additionalDetails, resolveSubmit } = action;
    const endpoint = URLS.schedule.updateReservationFeedback.replace('{reservationId}', reservationId);
    const feedback = feedbackOptions.map(opt => snakeCase(opt));

    const data = {
      feedback_type: 'negative',
      feedback_options: feedback,
      description: additionalDetails || null,
    };

    yield call([httpClient, httpClient.post], endpoint, data);
    yield all([call(resolveSubmit), put(closeModal()), put(createSuccessSnackbar(messages.success.feedbackSubmitted))]);
  } catch (error) {
    const { rejectSubmit } = action;
    yield call(rejectSubmit);
    if (error?.response?.data?.name === 'VENUE_USER_FEEDBACK_EXISTS') {
      yield all([put(closeModal()), put(createErrorSnackbar(messages.error.feedbackAlreadyExists))]);
    } else {
      yield put(createErrorBanner(messages.error.submittingFeedback));
    }
    yield call(reportError, error);
  }
}

export function* doActOnOpportunityFromSchedule(client, action) {
  const { venueId, schedule, opportunities, spotsAdded, startDateTime, source, showDemand } = action;
  const { scheduleId, spots } = schedule;
  const { classpassTotal } = spots || {};
  yield call(fetchEntity, venueOpportunities, [client, { venueId, scheduleId, opportunities, spotsAdded }]);
  yield all([
    call(doFetchVenueData, client, { venueId }),
    call(refreshVenueSchedulesCacheForVenueAndDate, { venueId, date: startDateTime }),
    call(invalidateScheduleDetailsV2Reads, venueId),
  ]);
  const newClasspassSpots = classpassTotal + spotsAdded;
  yield all([
    put(
      initialize(`schedule-detail-update-network-cap-${scheduleId}`, {
        classpassSpots: newClasspassSpots,
        actedOnOpportunity: true,
      })
    ),
    put(createInfoBanner(messages.success.spotsAdded, { spotsAdded })),
    call(trackAcceptedOpportunity, source, scheduleId, spotsAdded),
    call(trackOpportunityAccepted, { source: OPPORTUNITY_ACT_SOURCE.SCHEDULE, scheduleId }),
  ]);
  if (showDemand) {
    yield call(Segment.event, 'Demand Insights Spot Added', {
      scheduleId,
      venueId,
    });
  }
}

export function* doUpdateScheduleReservationsStopped(client, action) {
  try {
    const { httpClient } = client;
    const { scheduleId } = action;

    const venueId = yield select(selectActiveVenueId);
    const endpoint = URLS.schedule.setFull(venueId, scheduleId);

    const confirmed = yield call(waitForModal, client, 'MarkClassAsFullConfirmationModal');
    if (!confirmed) return;

    yield call([httpClient, httpClient.post], endpoint, { is_full: true });

    const startDate = yield select(selectSchedulesStartDate);
    yield all([
      call(doFetchVenueData, client, { venueId }),
      call(refreshVenueSchedulesCacheForVenueAndDate, { venueId, date: startDate }),
    ]);
    yield put(createInfoBanner(messages.success.classMarkedAsFull));
    yield call(trackScheduleReservationsStopped, { reservationsStopped: true, scheduleId });
  } catch (error) {
    const { scheduleId } = action;
    yield all([
      call(trackScheduleReservationsStoppedError, { scheduleId, error }),
      put(createErrorBanner(messages.error.updatingScheduleStatus)),
      call(reportError, error),
    ]);
  }
}

export function* doUpdateScheduleReservationsNotStopped(client, action) {
  try {
    const { httpClient } = client;
    const { scheduleId } = action;
    const venueId = yield select(selectActiveVenueId);
    const endpoint = URLS.schedule.setFull(venueId, scheduleId);

    const confirmed = yield call(waitForModal, client, 'MarkClassAsNotFullConfirmationModal');
    if (!confirmed) return;

    yield call([httpClient, httpClient.post], endpoint, { is_full: false });

    const startDate = yield select(selectSchedulesStartDate);
    yield all([
      call(doFetchVenueData, client, { venueId }),
      call(refreshVenueSchedulesCacheForVenueAndDate, { venueId, date: startDate }),
    ]);
    yield put(createInfoBanner(messages.success.classMarkedAsNotFull));
    yield call(trackScheduleReservationsStopped, { reservationsStopped: false, scheduleId });
  } catch (error) {
    const { scheduleId } = action;
    yield all([
      call(trackScheduleReservationsStoppedError, { scheduleId, error }),
      put(createErrorBanner(messages.error.updatingScheduleStatus)),
      call(reportError, error),
    ]);
  }
}

export function* doRedirectToManageClass(client, action) {
  let { venueId } = action;
  const { classId } = action;
  try {
    // venueId should be present but we're seeing errors because it's missing
    // try to save the situation
    if (!venueId) {
      venueId = yield select(selectActiveVenueId);
    }
    // don't make the call if we're still missing data
    if (!venueId) {
      // eslint-disable-next-line no-throw-literal
      throw { venueId };
    }
    const newPath = makePath.editClass(venueId, classId);
    yield call(Segment.event, 'Click Manage Class', { classId, venueId });
    yield call(client.navigate, newPath);
  } catch (error) {
    yield put(createErrorBanner(messages.error.redirectToManageClass));
    if (!(error instanceof Error)) {
      yield call(reportError, 'caught doRedirectToManageClass', error);
    }
  }
}

export function* doRedirectToManageSchedule(client, action) {
  const { httpClient } = client;
  const { venueId } = action || (yield select(selectActiveVenueId));
  const { apiGuid, startDate, startDateTime } = action;
  try {
    const getMSIScheduleIdEndpoint = URLS.msSchedules.getParentMSIScheduleId(venueId, apiGuid);
    const {
      data: { schedule_id: scheduleId },
    } = yield call([httpClient, httpClient.get], getMSIScheduleIdEndpoint);
    const { scheduleType } = yield call(fetchEntity, scheduleData, [client, venueId, scheduleId]);

    const editSchedulePath =
      scheduleType === 'recurring'
        ? makePath.scheduleInstanceEdit({
            venueId,
            scheduleId,
            occurrenceDate: startDate,
            pageSource: ROSTER_PAGE_SOURCE,
          })
        : makePath.schedulesEdit({ venueId, startDate: startDateTime, id: scheduleId, pageSource: ROSTER_PAGE_SOURCE });

    yield call(client.navigate, editSchedulePath);
  } catch (error) {
    yield all([put(createErrorBanner(messages.error.redirectToManageSchedule)), call(reportError, error)]);
  }
}

// Legacy details have no Insights tab off-desktop; details v2 renders it on every viewport.
export function* isInsightsTabHiddenOnMobile({ isDesktop, tab, venueId }) {
  if (isDesktop || tab !== 'insights') {
    return false;
  }

  const isDetailsV2Enabled = yield call(isScheduleDetailsV2EnabledForVenue, venueId);
  return !isDetailsV2Enabled;
}

export function* handleRouteEnter(client) {
  yield call(waitForNavigate, client);

  const user = yield call(fetchEntityIfNeeded, userInfo, [client]);
  const startDate = yield call(getStartDateOrToday);
  const isDesktop = yield select(selectIsDesktop);
  const tab = yield select(selectActiveScheduleDetailTab);
  const lastActiveVenueId = yield select(selectLastActiveVenueId);
  let venueId = yield select(selectActiveVenueId);
  let scheduleId = yield select(selectActiveScheduleId);
  let newPath;

  if (!userHasAccessToVenue(user, venueId)) {
    ({ venueId } = getFirstLinkedVenue(user));
    newPath = makePath.classes({ venueId, startDate });
  }

  // the user changed venues on another page and the new venueId & old scheduleId don't match
  if (lastActiveVenueId && lastActiveVenueId !== venueId) {
    scheduleId = null;
    yield all([put(setLastActiveVenueId(venueId)), put(removeSelectedScheduleId())]);
  }

  const hidesInsightsOnMobile = yield call(isInsightsTabHiddenOnMobile, { isDesktop, tab, venueId });

  if (!newPath && scheduleId && (!tab || hidesInsightsOnMobile)) {
    newPath = makePath.classes({ venueId, startDate, scheduleId, tab: 'roster', focus: 'list' });
  }

  const { pathname, search } = client.location;
  const currentPath = pathname + search;
  if (newPath && newPath !== currentPath) {
    yield call(client.navigate, newPath, { replace: true });
  }

  yield call(doFetchVenueData, client, { venueId, startDate });
}

export function* handleViewWaiverRouteEnter(dependencies) {
  const { venueId, scheduleId, waiverId, waiverVersionId } = yield select(selectRouteParams);
  yield call(fetchEntityIfNeeded, roster, [dependencies, venueId, scheduleId]);
  yield call(fetchEntityIfNeeded, waivers, [dependencies, waivers.method.latest, venueId]);
  yield call(fetchEntityIfNeeded, waivers, [dependencies, waivers.method.version, venueId, waiverId, waiverVersionId]);
}

export function* handleActiveVenueChange(client, action) {
  const { venueId } = action;
  const startDate = yield select(selectSchedulesStartDate);
  const newPath = makePath.classes({ venueId, startDate });

  yield all([put(removeSelectedScheduleId()), call(client.navigate, newPath)]);
  // We need the history to update before re-entering the route but we don't want to use locationSagas
  // on the route because changes to the scheduleId cause a lot of unnecessary API calls.
  yield delay(1);
  yield call(handleRouteEnter, client);
}

export function* sendEmail(
  { httpClient },
  { resolveSubmit, rejectSubmit },
  { endpoint, recipients, messageType, subject, messageContent }
) {
  const payload = {
    email: {
      messageType,
      messageContent: messageContent.replace(/\n/g, '<br />'),
      subject,
    },
  };

  if (recipients) {
    payload.recipients = recipients;
  }

  try {
    yield call([httpClient, httpClient.post], endpoint, transformKeysToSnakeCase(payload));
    const { venueId } = yield select(selectActiveVenue);
    yield all([
      call(resolveSubmit),
      put(closeModal()),
      put(createInfoBanner(messages.success.sendingEmail)),
      call(Segment.event, 'Initiate email message', {
        venueId,
        type: EMAIL_MESSAGE_TYPE_BY_EMAIL_TYPE[messageType],
      }),
    ]);
  } catch (error) {
    yield all([call(rejectSubmit), put(createErrorBanner(messages.error.sendingEmail))]);
  }
}

export function* doEmailMember(dependencies, action) {
  const { intl } = dependencies;
  const { message: messageContent, startDateTime, recipients } = action;
  const { venueId, venueName, venueSubtitle } = yield select(selectActiveVenue);

  yield call(sendEmail, dependencies, action, {
    endpoint: URLS.schedule.notifyUsers(venueId),
    messageType: EMAIL_TYPES.GENERIC_TO_SOME_USERS,
    subject: intl.formatMessage(messages.email.subject, {
      startDateTime,
      venueName,
      venueSubtitle: venueSubtitle || undefined,
    }),
    messageContent,
    recipients,
  });
}

export function* doEmailScheduleAttendees(dependencies, action) {
  const { intl } = dependencies;
  const { venueId, venueName, venueSubtitle } = yield select(selectActiveVenue);
  const { schedule, message: messageContent, newAttendeesOnly, recipients } = action;
  const { scheduleId, startDateTime } = schedule;
  const payload = {
    endpoint: URLS.schedule.notifyScheduleUsers(venueId, scheduleId),
    messageType: newAttendeesOnly ? EMAIL_TYPES.GENERIC_TO_NEW_USERS : EMAIL_TYPES.GENERIC,
    subject: intl.formatMessage(messages.email.subject, {
      startDateTime,
      venueName,
      venueSubtitle: venueSubtitle || undefined,
    }),
    messageContent,
    recipients,
  };

  if (recipients) {
    payload.endpoint = URLS.schedule.notifyUsers(venueId);
    payload.messageType = EMAIL_TYPES.GENERIC_TO_SOME_USERS;
    payload.recipients = recipients;
  }

  yield call(sendEmail, dependencies, action, payload);
}

function* refreshRosterAfterReservationFeedbackSubmit(dependencies, action) {
  const { venueId, scheduleId } = action;
  if (venueId == null || scheduleId == null) {
    return;
  }
  try {
    yield call(fetchEntity, roster, [dependencies, venueId, scheduleId]);
  } catch (error) {
    yield call(reportError, error);
  }
}

export function* watchActions(client) {
  yield takeEvery(FETCH_SCHEDULE_DATA, doFetchScheduleData, client);
  yield takeEvery(FETCH_VENUE_DATA, doFetchVenueData, client);
  yield takeEvery(ACTIVE_VENUE_CHANGED, handleActiveVenueChange, client);
  yield takeEvery(SET_SELECTED_SCHEDULE, doSetSelectedSchedule, client);
  yield takeEvery(SET_SELECTED_TAB, doSetSelectedTab, client);
  yield takeEvery(UPDATE_START_DATE, doUpdateStartDate, client);
  yield takeEvery(UPDATE_SCHEDULE_NETWORK_ALLOCATION, doUpdateScheduleNetworkAllocation, client);
  yield takeEvery(CANCEL_SCHEDULE, doCancelSchedule, client);
  yield takeEvery(ENABLE_SCHEDULE, doEnableSchedule, client);
  yield takeEvery(UPDATE_RESERVATION_ATTENDANCE, doUpdateReservationAttendance, client);
  yield takeEvery(UPDATE_RESERVATION_FEEDBACK, doUpdateReservationFeedback, client);
  yield takeEvery(ACT_ON_OPPORTUNITY_FROM_SCHEDULE, doActOnOpportunityFromSchedule, client);
  yield takeEvery(EMAIL_SCHEDULE_ATTENDEES, doEmailScheduleAttendees, client);
  yield takeEvery(UPDATE_SCHEDULE_RESERVATIONS_STOPPED, doUpdateScheduleReservationsStopped, client);
  yield takeEvery(UPDATE_SCHEDULE_RESERVATIONS_NOT_STOPPED, doUpdateScheduleReservationsNotStopped, client);
  yield takeEvery(REDIRECT_TO_MANAGE_CLASS, doRedirectToManageClass, client);
  yield takeEvery(REDIRECT_TO_MANAGE_SCHEDULE, doRedirectToManageSchedule, client);
  yield takeEvery(OPEN_CANCEL_SCHEDULE_MODAL, doOpenCancelScheduleModal, client);
  yield takeEvery(ADD_SPOT, doAddSpot, client);
  yield takeEvery(EMAIL_MEMBER, doEmailMember, client);
  yield takeEvery(REFRESH_ROSTER_AFTER_RESERVATION_FEEDBACK, refreshRosterAfterReservationFeedbackSubmit, client);
}
